import { ReactNode } from 'react'
import cx from 'classnames'

import FormItem from 'common/components/formItem'
import TextareaComponent from 'common/components/textarea'

import { CommonFieldPropsGeneric, TextareaType } from '../interfaces'

type Props = CommonFieldPropsGeneric & {
  value: TextareaType['value']
  max: TextareaType['max']
  min: TextareaType['min']
  componentProps?: TextareaType['componentProps']
  // Rendered inside the same bordered box as the textarea, below the text —
  // e.g. the paste-HTML actions row — so it reads as one field, not two.
  footer?: ReactNode
  // Stable id for the field's <label>, for third-party tools (e.g. Intercom
  // tooltips) that need a fixed target instead of matching on label text.
  labelId?: string
}

const Textarea = ({
  max,
  min,
  label,
  name,
  value,
  errorCode,
  tooltip,
  isDisabled,
  placeholder,
  componentProps,
  footer,
  labelId,
  onChange,
  'data-testid': dataTestId,
  ...rest
}: Props) => {
  const ERROR_MESSAGES = {
    required: 'Please enter an answer.',
    min: `Please type at least ${min} character${min === 1 ? '' : 's'}.`,
  }

  const textarea = (
    <TextareaComponent
      id={name}
      name={name}
      data-testid={dataTestId}
      placeholder={placeholder}
      disabled={isDisabled}
      onChange={(event) => onChange(event.target.value)}
      value={value}
      maxLength={max}
      isError={footer ? !!errorCode : undefined}
      {...(footer && {
        customClassName:
          'flex-1 resize-none bg-transparent text-base-700 font-normal text-sm leading-4 px-4 pt-3 placeholder-gold-700 outline-none w-full',
      })}
      {...componentProps}
      {...rest}
    />
  )

  return (
    <FormItem
      label={label}
      labelId={labelId}
      guidingText={tooltip}
      htmlFor={name}
      error={errorCode ? ERROR_MESSAGES[errorCode] : undefined}
    >
      {footer ? (
        <div
          className={cx(
            'flex flex-col resize-y overflow-auto min-h-[9rem] rounded-sm bg-gold-40 border',
            errorCode ? 'border-RAGred' : 'border-gold-400',
            { 'focus-within:border-base-700': !errorCode && !isDisabled }
          )}
        >
          {/* Without this wrapper, the textarea's own flex-1 only stretches
              within its immediate (unstretched) parent, so it shrinks to fit
              the text and the footer ends up stranded mid-box instead of
              pinned to the bottom when this box is resized taller. Grid (not
              flex) so the single child stretches on BOTH axes — a flex row
              here would only stretch it vertically (the cross axis) and
              leave it at its intrinsic content width horizontally. */}
          <div className="flex-1 min-h-0 grid">{textarea}</div>
          <div className="px-4 pb-3 shrink-0">{footer}</div>
        </div>
      ) : (
        textarea
      )}
    </FormItem>
  )
}

export default Textarea
